import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, TriangleAlert, OctagonX, AlertOctagon, Clock, CalendarClock, ArrowRight, Sunrise, Zap, ClipboardCheck } from "lucide-react";
import api from "../lib/api";
import { withCache } from "../lib/offlineStore";
import { useAuth } from "../context/AuthContext";
import { FrontCard } from "../components/FrontCard";
import { Button } from "../components/ui/button";
import { APP } from "../constants/testIds";

const KpiCard = ({ icon: Icon, label, value, testId, accent }) => (
  <div data-testid={testId} className={`rounded-xl border border-slate-800 bg-[#121820] p-4 flex items-center gap-3`}>
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${accent}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <div className="text-2xl font-black text-slate-100 leading-none">{value}</div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-1">{label}</div>
    </div>
  </div>
);

export default function Home() {
  const { selectedSite } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [fronts, setFronts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBimensuellePicker, setShowBimensuellePicker] = useState(false);
  const [pickedFront, setPickedFront] = useState("");

  useEffect(() => {
    setLoading(true);
    const q = `?site=${selectedSite}`;
    Promise.all([
      withCache(`dashboard_summary_${selectedSite}`, () => api.get(`/dashboard/summary${q}`).then((r) => r.data)),
      withCache(`alerts_${selectedSite}`, () => api.get(`/alerts${q}`).then((r) => r.data)),
      withCache(`fronts_priority_${selectedSite}`, () => api.get(`/fronts?sort=priority&site=${selectedSite}`).then((r) => r.data)),
    ]).then(([s, a, f]) => {
      setSummary(s.data);
      setAlerts(a.data);
      setFronts(f.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [selectedSite]);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center text-slate-500 font-mono uppercase text-sm">Chargement des fronts...</div>;
  }

  if (!summary) {
    return <div className="min-h-[60vh] flex items-center justify-center text-slate-500 font-mono uppercase text-sm">Aucune donnée disponible hors ligne.</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.25em] text-cyan-500 mb-1">Tableau de bord</div>
        <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tight text-slate-50">État des fronts</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        <KpiCard testId={APP.kpiAutorises} icon={ShieldCheck} label="Autorisés" value={summary.fronts_autorises} accent="bg-emerald-500/15 text-emerald-400" />
        <KpiCard testId={APP.kpiReserves} icon={TriangleAlert} label="Avec réserves" value={summary.fronts_reserves} accent="bg-amber-500/15 text-amber-400" />
        <KpiCard testId={APP.kpiSuspendus} icon={OctagonX} label="Suspendus" value={summary.fronts_suspendus} accent="bg-red-500/15 text-red-400" />
        <KpiCard testId={APP.kpiAnomaliesCritiques} icon={AlertOctagon} label="Anomalies critiques" value={summary.anomalies_critiques_ouvertes} accent="bg-red-500/15 text-red-400" />
        <KpiCard testId={APP.kpiActionsRetard} icon={Clock} label="Actions en retard" value={summary.actions_en_retard} accent="bg-orange-500/15 text-orange-400" />
        <KpiCard testId={APP.kpiInspectionsJour} icon={CalendarClock} label="Inspections à réaliser" value={summary.inspections_a_realiser_aujourdhui} accent="bg-cyan-500/15 text-cyan-400" />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="rounded-xl border border-slate-800 bg-[#121820] p-4 flex flex-col gap-3 hover:border-cyan-500/50 transition-colors">
          <button
            type="button"
            data-testid="home-link-inspection-bimensuelle"
            onClick={() => setShowBimensuellePicker(!showBimensuellePicker)}
            className="flex items-center gap-3 text-left"
          >
            <div className="w-10 h-10 rounded-lg bg-emerald-500/15 text-emerald-400 flex items-center justify-center shrink-0"><ClipboardCheck className="w-5 h-5" /></div>
            <div>
              <div className="text-sm font-bold text-slate-100">Inspection bimensuelle des fronts</div>
              <div className="text-xs text-slate-500">Contrôle complet (18 questions)</div>
            </div>
          </button>
          {showBimensuellePicker && (
            <div className="flex gap-2">
              <select
                data-testid="home-inspection-bimensuelle-front-select"
                value={pickedFront}
                onChange={(e) => setPickedFront(e.target.value)}
                className="flex-1 bg-[#0A0D12] border border-slate-700 rounded-md h-9 px-2 text-xs text-slate-100"
              >
                <option value="">— Choisir un front —</option>
                {fronts.map((f) => <option key={f.Front_ID} value={f.Front_ID}>{f.Front_Libelle}</option>)}
              </select>
              <Button
                data-testid="home-inspection-bimensuelle-go-button"
                size="sm"
                disabled={!pickedFront}
                onClick={() => navigate(`/inspection/new/${pickedFront}`)}
                className="bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-bold uppercase"
              >
                Aller
              </Button>
            </div>
          )}
        </div>
        <Link to="/visite/nouvelle/avant-poste" data-testid="home-link-visite-avant-poste" className="rounded-xl border border-slate-800 bg-[#121820] p-4 flex items-center gap-3 hover:border-cyan-500/50 transition-colors">
          <div className="w-10 h-10 rounded-lg bg-cyan-500/15 text-cyan-400 flex items-center justify-center shrink-0"><Sunrise className="w-5 h-5" /></div>
          <div>
            <div className="text-sm font-bold text-slate-100">Visite avant prise de poste</div>
            <div className="text-xs text-slate-500">Contrôle quotidien avant chargement</div>
          </div>
        </Link>
        <Link to="/visite/nouvelle/apres-tir" data-testid="home-link-visite-apres-tir" className="rounded-xl border border-slate-800 bg-[#121820] p-4 flex items-center gap-3 hover:border-cyan-500/50 transition-colors">
          <div className="w-10 h-10 rounded-lg bg-orange-500/15 text-orange-400 flex items-center justify-center shrink-0"><Zap className="w-5 h-5" /></div>
          <div>
            <div className="text-sm font-bold text-slate-100">Visite après tir de mine</div>
            <div className="text-xs text-slate-500">Contrôle post-tir avant accès</div>
          </div>
        </Link>
      </div>

      {alerts.length > 0 && (
        <div data-testid={APP.priorityAlertsContainer} className="rounded-2xl border-2 border-red-600/50 bg-red-950/30 p-5">
          <h2 className="text-sm font-black uppercase tracking-widest text-red-300 mb-3 flex items-center gap-2">
            <AlertOctagon className="w-4 h-4" /> À traiter en priorité
          </h2>
          <div className="space-y-2">
            {alerts.slice(0, 8).map((a, idx) => {
              const severe = a.level === "RED" && a.days_overdue > 5;
              return (
                <Link
                  key={idx}
                  to={`/front/${a.front_id}`}
                  data-testid={`priority-alert-item-${idx}`}
                  className={`flex items-center justify-between gap-3 rounded-lg px-4 py-2.5 border transition-colors ${
                    severe
                      ? "border-red-600 bg-red-600/15 hover:bg-red-600/25 shadow-[0_0_18px_-6px_rgba(220,38,38,0.8)]"
                      : a.level === "RED" ? "border-red-500/40 bg-red-500/5 hover:bg-red-500/10" : "border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10"
                  }`}
                >
                  <span className="text-sm text-slate-200">
                    <span className="mr-2">{a.level === "RED" ? "🔴" : "🟠"}</span>
                    <b>{a.front_libelle}</b> — {a.message}
                    {severe && (
                      <span data-testid={`priority-alert-severe-badge-${idx}`} className="ml-2 inline-block text-[10px] font-black uppercase tracking-widest text-red-200 bg-red-700/60 border border-red-500 rounded-full px-2 py-0.5">
                        Urgent
                      </span>
                    )}
                  </span>
                  <ArrowRight className="w-4 h-4 text-slate-500 shrink-0" />
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <h2 className="text-xl font-bold uppercase tracking-tight text-slate-100 mb-4">Fronts actifs</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {fronts.map((f) => <FrontCard key={f.Front_ID} front={f} />)}
        </div>
      </div>
    </div>
  );
}
